import './style.css';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ANCHORS, anchorOf, CATEGORIES, dateRange as getDateRange } from './mock/data';
import { fetchAnchors, searchGoods } from './dataSource';
import type { Anchor, PagePreviewState, ProductQuery, SessionProduct } from './mock/types';
import { OfferPrice } from './components/ProductCard';
import DetailModal from './components/DetailModal';
import HostPickerModal from './components/HostPickerModal';
import { EmptyState, SkeletonList } from './state';
import { withGhId } from './apiConfig';
import ScrollLoader from './components/ScrollLoader';
import { usePageView } from './usePageView';

const STATUS: Record<string, { cls: string; label: string }> = {
  living: { cls: 'living', label: '直播中' },
  pending: { cls: 'pending', label: '待开播' },
  ended: { cls: 'ended', label: '已结束' },
};

/** S01/S03：时间范围档位（与接口 date_range 对齐） */
type DateRange = 'today7' | 'today' | 'all';
type SearchParams = Parameters<typeof searchGoods>[0];
const RANGE_OPTS: { v: DateRange; label: string }[] = [
  { v: 'today7', label: '直播中及未来7天' },
  { v: 'today', label: '仅今天（含已结束）' },
  { v: 'all', label: '所有已发布预告（含历史）' },
];

const KW_MAX = 80; // S02：最多 80 字符
const HISTORY_KEY = 'v2_search_history';
const HISTORY_MAX = 8; // S06：最近 8 个非空词

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x: unknown) => typeof x === 'string').slice(0, HISTORY_MAX) : [];
  } catch {
    return []; // 读取失败按无历史处理，不阻断
  }
}

function saveHistory(words: string[]): boolean {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(words));
    return true;
  } catch {
    return false; // S06/T15：存储失败不阻断查询
  }
}

/** 搜索匹配（S02）：商品名/品牌/主播名/序号；英文忽略大小写、去首尾空格 */
function matchKeyword(g: SessionProduct, kw: string): boolean {
  const k = kw.trim().toLowerCase();
  if (!k) return true;
  const anchor = anchorOf(g.session.anchorId);
  return [g.itemName, g.brand, anchor.name, String(g.linkNo)]
    .some((f) => f.toLowerCase().includes(k) || String(g.linkNo) === k);
}

function scopeFromUrl(): 'all' | 'anchor' {
  const m = window.location.search.match(/[?&]scope=(all|anchor)/);
  return (m && m[1] === 'anchor' ? 'anchor' : 'all');
}
function kwFromUrl(): string {
  const m = window.location.search.match(/[?&]q=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

/** 全站/单主播搜索（S01–S06）：常驻范围切换；提交后记录历史；历史点击即搜 */
const TrailerSearch: FunctionalComponent = () => {
  usePageView('search');
  const [scope, setScope] = useState<'all' | 'anchor'>(scopeFromUrl());
  const [anchorId, setAnchorId] = useState<number>(() => Number(new URLSearchParams(window.location.search).get('anchor')) || 1);
  const [anchors, setAnchors] = useState<Anchor[]>(ANCHORS);
  const [kw, setKw] = useState<string>(kwFromUrl());
  /** 已提交生效的词（S02：非空提交才进历史并展示结果） */
  const [submitted, setSubmitted] = useState<string>(kw.trim());
  const [results, setResults] = useState<SessionProduct[]>([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [history, setHistory] = useState<string[]>(loadHistory);
  const [historyToast, setHistoryToast] = useState<string | null>(null);
  const [query, setQuery] = useState<ProductQuery>({});
  const [dateRange, setDateRange] = useState<DateRange>('today7');
  const [rangeMenu, setRangeMenu] = useState(false);
  const [preview, setPreview] = useState<PagePreviewState>('normal');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  /** 主播信息以接口列表为准（与主页一致），静态 ANCHORS 仅兜底 */
  const anchorById = (id: number): Anchor => anchors.find((a) => a.id === id) ?? anchorOf(id);

  const searchRequest = useRef(0);
  const searchPage = useRef<{ params: SearchParams; page: number; request: number; hasMore: boolean } | null>(null);
  const moreBusy = useRef(false);
  useEffect(() => () => { searchRequest.current++; }, []);
  const searched = submitted !== '';
  const list = results;
  const recentDates = getDateRange(7);
  const detail = detailId ? list.find((g) => g.sessionProductId === detailId) ?? null : null;

  /** S02/S06：提交（按钮/回车/点击历史词）。空白提交回到未搜索状态不弹错误 */
  const doSearch = (word: string, override?: { scope?: 'all' | 'anchor'; anchorId?: number; dateRange?: DateRange; categoryId?: string | null }) => {
    const request = ++searchRequest.current;
    searchPage.current = null;
    moreBusy.current = false;
    setHasMore(false);
    setLoadingMore(false);
    setMoreFailed(false);
    const w = word.trim().slice(0, KW_MAX);
    const sc = override?.scope ?? scope;
    const aid = override?.anchorId ?? anchorId;
    const dr = override?.dateRange ?? dateRange;
    const ct = override && 'categoryId' in override ? (override.categoryId ?? 'all') : query.categoryId;
    setKw(w);
    setSubmitted(w);
    if (w === '') { setResults([]); setPreview('normal'); return; }
    setPreview('loading'); // T10
    const params: SearchParams = {
      scope: sc,
      scene: anchorById(aid).scene ?? '',
      keyword: w,
      dateRange: dr,
      categoryId: ct,
      page: 1,
    };
    searchGoods(params).then((ret) => {
      if (request !== searchRequest.current) return;
      searchPage.current = { params, page: 1, request, hasMore: !!ret.hasMore };
      setResults(ret.list);
      setResultTotal(ret.total);
      setHasMore(!!ret.hasMore);
      setPreview('normal');
    }).catch(() => { if (request === searchRequest.current) setPreview('failed'); });
    if (w === '') return;
    setHistory((h) => {
      const next = [w, ...h.filter((x) => x !== w)].slice(0, HISTORY_MAX); // 重复词移至最前
      const ok = saveHistory(next);
      if (!ok) {
        // T15：短提示约 2.7 秒自动消失，不阻断
        setHistoryToast('当前环境不支持保存，关闭后记录可能丢失');
        setTimeout(() => setHistoryToast(null), 2700);
      }
      return next;
    });
  };

  const loadMore = async (): Promise<void> => {
    const active = searchPage.current;
    if (!active || active.request !== searchRequest.current || !active.hasMore || moreBusy.current) return;
    moreBusy.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const nextPage = active.page + 1;
    try {
      const ret = await searchGoods({ ...active.params, page: nextPage });
      if (active.request !== searchRequest.current) return;
      setResults((previous) => [...previous, ...ret.list.filter((item) => !previous.some((p) => p.sessionProductId === item.sessionProductId))]);
      active.page = nextPage;
      active.hasMore = !!ret.hasMore;
      setResultTotal(ret.total);
      setHasMore(!!ret.hasMore);
    } catch (_) {
      if (active.request === searchRequest.current) setMoreFailed(true);
    } finally {
      if (active.request === searchRequest.current) {
        moreBusy.current = false;
        setLoadingMore(false);
      }
    }
  };

  /** S06：清空历史无需二次确认；只删历史不动当前范围/词 */
  const clearHistory = () => {
    setHistory([]);
    const ok = saveHistory([]);
    if (!ok) {
      setHistoryToast('当前环境不支持保存，关闭后记录可能丢失');
      setTimeout(() => setHistoryToast(null), 2700);
    } else {
      setHistoryToast('搜索历史已清空');
      setTimeout(() => setHistoryToast(null), 2700);
    }
  };

  // 深链带词时视为已提交；主播列表拉真实数据（与主页一致）
  useEffect(() => {
    fetchAnchors().then(setAnchors).catch(() => undefined);
    if (kw.trim() !== '') doSearch(kw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** S03：更换时间范围，保留已提交关键词与列表筛选并重新查询 */
  const pickRange = (dr: DateRange) => {
    setRangeMenu(false);
    if (dr === dateRange) return;
    setDateRange(dr);
    if (submitted) doSearch(submitted, { dateRange: dr });
  };

  /** P01：分类切换立即生效并重查（接口模式由服务端过滤） */
  const pickCategory = (id: string) => {
    setQuery({ ...query, categoryId: id });
    if (submitted) doSearch(submitted, { categoryId: id });
  };

  return (
    <div class="vp-page">

      <div class="vp-topbar">
        <button class="vp-topbar-back" onClick={() => window.history.back()} aria-label="返回">‹</button>
        <input
          class="vp-topbar-search"
          value={kw}
          maxLength={KW_MAX}
          placeholder="商品 / 品牌 / 序号"
          onInput={(e) => setKw((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch(kw)}
        />
        <button class="vp-topbar-btn" onClick={() => doSearch(kw)}>搜索</button>
      </div>

      <div class="vp-scope-row">
        <button class={`vp-scope-btn${scope === 'all' ? ' selected' : ''}`} onClick={() => { setScope('all'); setQuery({}); if (submitted) doSearch(submitted, { scope: 'all', categoryId: null }); }}>全站</button>
        <button class={`vp-scope-btn${scope === 'anchor' ? ' selected' : ''}`} onClick={() => { setScope('anchor'); setQuery({}); if (submitted) doSearch(submitted, { scope: 'anchor', categoryId: null }); }}>{anchorById(anchorId).name}</button>
        <button class="vp-scope-more" onClick={() => setPickerOpen(true)} aria-label="选择主播">
          <i class="vp-scope-caret" />
        </button>
      </div>

      <div class="vp-daterange-row">
        <span class="vp-daterange-label">搜索日期范围</span>
        <div class="vp-daterange-wrap">
          <button class="vp-daterange-value" onClick={() => setRangeMenu((v) => !v)}>
            {RANGE_OPTS.find((o) => o.v === dateRange)?.label}<i class="vp-daterange-caret">▾</i>
          </button>
          {rangeMenu && (
            <>
              <div class="vp-daterange-mask" onClick={() => setRangeMenu(false)} />
              <div class="vp-daterange-menu" role="menu">
                {RANGE_OPTS.map((o) => (
                  <button key={o.v} class={`vp-daterange-opt${o.v === dateRange ? ' on' : ''}`} onClick={() => pickRange(o.v)}>
                    <i class="vp-daterange-check">✓</i>{o.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {preview === 'loading' ? (
        <>
          <SkeletonList label="正在搜索商品预告…" />
        </>
      ) : preview === 'failed' ? (
        <EmptyState spec={{ title: '搜索失败，请重试', actions: [{ label: '重新搜索', kind: 'reload' }] }} onAction={() => doSearch(submitted || kw)} />
      ) : !searched ? (
        // S06：最近搜索区（无历史时显示空提示，不伪装成个人记录）
        <>
          <div class="vp-history-sec">
            <div class="vp-history-head">
              <b>最近搜索</b>
              {history.length > 0 && (
                <button class="vp-history-trash" onClick={clearHistory} aria-label="清空搜索历史">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6l1 14h10l1-14" /><path d="M10 11v6" /><path d="M14 11v6" />
                  </svg>
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <span class="vp-empty-desc">暂无搜索历史，输入关键词开始搜索</span>
            ) : (
              <div class="vp-history-list">
                {history.map((w) => (
                  <button key={w} class="vp-history-chip" onClick={() => doSearch(w)}>{w}</button>
                ))}
              </div>
            )}
          </div>
          <div class="vp-scope-notice">
            {scope === 'all' ? '当前搜索全部主播，结果将标注主播和场次。' : `当前仅搜索「${anchorById(anchorId).name}」直播间的商品预告。`}
          </div>
        </>
      ) : list.length === 0 ? (
        <EmptyState
          spec={{
            title: '没有找到相关预告',
            desc: scope === 'anchor' ? '可以扩大到全站，或换个商品名称试试。' : '试试品牌名、商品关键词或链接序号。',
            actions: scope === 'anchor' ? [{ label: '扩大到全站搜索', kind: 'scope-all' }, { label: '清除筛选条件', kind: 'clear' }] : [{ label: '清除筛选条件', kind: 'clear' }],
          }}
          onAction={(kind) => {
            if (kind === 'scope-all') { setScope('all'); setQuery({}); doSearch(submitted, { scope: 'all', categoryId: null }); } // S03：切范围清列表筛选
            if (kind === 'clear') { setQuery({}); doSearch(submitted, { categoryId: null }); }
          }}
        />
      ) : (
        <>
          <div class="vp-category">
            {CATEGORIES.map((c) => (
              <button key={c.id} class={`vp-category-item${(query.categoryId ?? 'all') === c.id ? ' selected' : ''}`} onClick={() => pickCategory(c.id)}>
                {c.label}
              </button>
            ))}
          </div>
          <div class="vp-metarow">
            <span>“{submitted}” · {resultTotal} 条预告 · <b class="vp-chip pending">{scope === 'all' ? '全站' : anchorById(anchorId).name}</b></span>
          </div>
          {list.map((g) => {
            const anchor = anchorById(g.session.anchorId);
            const s = g.session;
            // 历史数据可能一直保留 pending；仅近期 activity_date 展示待开播标签。
            const chip = s.status === 'pending' && !recentDates.includes(s.date) ? null : STATUS[s.status];
            const best = g.offers.find((o) => o.price) ?? g.offers[0];
            return (
              <div key={g.sessionProductId} class="vp-productrow">
                <button class="vp-productcard-figure" onClick={() => setDetailId(g.sessionProductId)}>
                  <img src={g.image} alt={g.itemName} />
                  <span class="vp-linkno">{g.linkNo}号</span>
                </button>
                <div class="vp-productrow-body">
                  <button class="vp-productrow-title vp-productcard-title" onClick={() => setDetailId(g.sessionProductId)}>
                    {g.itemName}
                  </button>
                  <span class="vp-result-source">
                    {anchor.name} · {parseInt(s.date.slice(5, 7), 10)}/{parseInt(s.date.slice(8), 10)} {s.startTime}
                    {chip && <> · <span class={`vp-chip ${chip.cls}`}>{chip.label}</span></>}
                  </span>
                  <span class="vp-result-spec">{best?.name ?? ''}</span>
                  <div class="vp-price-row"><OfferPrice price={best?.price ?? null} unit={best?.toHandUnit ?? ''} /></div>
                  <button class="vp-card-btn compact solid" onClick={() => setDetailId(g.sessionProductId)}>看详情</button>
                </div>
              </div>
            );
          })}
          <ScrollLoader hasMore={hasMore} loading={loadingMore} failed={moreFailed}
            loaded={list.length} total={resultTotal} onLoad={loadMore} endText="已展示全部搜索结果" />
        </>
      )}

      {historyToast && <div class="vp-toast">{historyToast}</div>}

      <div class="vp-tab-content-pad" />

      <HostPickerModal
        open={pickerOpen}
        anchors={anchors}
        currentAnchorId={anchorId}
        onCancel={() => setPickerOpen(false)}
        onPick={(a) => {
          setAnchorId(a.id);
          setQuery({}); // P01：换主播清商品筛选，保留词与时间
          setScope('anchor');
          setPickerOpen(false);
          if (submitted) doSearch(submitted, { scope: 'anchor', anchorId: a.id, categoryId: null }); // S03
        }}
      />
      <DetailModal
        product={detail}
        anchorName={detail ? anchorOf(detail.session.anchorId).name : ''}
        onViewSession={(prod) => { setDetailId(null); location.href = withGhId(`/live?anchor=${prod.session.anchorId}&date=${prod.session.date}&session=${prod.session.id}`); }}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};


export default TrailerSearch;
